import type { RecordKind } from "@baton/core";
import { createContext, useCallback, useContext, type ComponentType, type ReactNode } from "react";

/**
 * FR-015-150 § URL schemes as amended 2026-10-15: the host's record reference, the component that
 * resolves a record link's page in the host's route table and draws `RecordPill` with that address.
 * It is a module of its own, apart from the pill, so a host that provides it at its shell carries
 * the context alone in its entry and may hand over a lazily loaded component (`RecordLinkPill`
 * draws the pill without its address while that component loads).
 */
export interface RecordReferenceProps {
  readonly kind: RecordKind;
  readonly id: string;
  readonly label: string;
}

/** Draws one record link with the host's record reference. */
export type RenderRecordReference = (props: RecordReferenceProps) => ReactNode;

const RecordReferenceContext = createContext<RenderRecordReference | null>(null);

/** Gives every `Markdown` and editor below it the host's record reference. */
export function RecordReferenceProvider({ component: Reference, children }: { readonly component: ComponentType<RecordReferenceProps>; readonly children: ReactNode }) {
  const render = useCallback<RenderRecordReference>((props) => <Reference {...props} />, [Reference]);
  return <RecordReferenceContext value={render}>{children}</RecordReferenceContext>;
}

/** The host's record reference, or null without a host. */
export function useHostRecordReference(): RenderRecordReference | null {
  return useContext(RecordReferenceContext);
}
